import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import { Badge } from '~/components/ui/badge';
import { Card, CardDescription, CardHeader, CardTitle } from '~/components/ui/card';
import { Page, PageHeader, Section, EmptyState } from '~/components/ui/page';
import { api } from '~/lib/api';
import type { PracticeSummary } from '@resonance/shared-types';

export const Route = createFileRoute('/_authed/practices/')({
  component: PracticesIndex,
});

const KIND_DESC: Record<string, { label: string; oneLiner: string; kicker: string }> = {
  forgiveness: {
    label: 'Forgiveness',
    kicker: '5 sessions',
    oneLiner:
      'name → harm done → context → unmet need → offering. ends with a letter to your former self.',
  },
  compassion: {
    label: 'Compassion',
    kicker: '21 days · 3 min',
    oneLiner:
      'rotating micro-practice. loving-kindness, somatic compassion, shared humanity.',
  },
  narrative: {
    label: 'Narrative',
    kicker: '6 chapters',
    oneLiner:
      "where you came from. what shaped you. what broke. what held. what you became. what you're becoming.",
  },
};

function PracticesIndex() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const { data } = useQuery({ queryKey: ['practices'], queryFn: api.listPractices });
  const [pendingKind, setPendingKind] = useState<string | null>(null);
  const [subject, setSubject] = useState('');

  const start = useMutation({
    mutationFn: (input: { kind: 'forgiveness' | 'compassion' | 'narrative'; subject?: string }) =>
      api.startPractice(input),
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: ['practices'] });
      nav({ to: '/practices/$id', params: { id: p.id } });
    },
  });

  const list = data?.practices ?? [];
  const active = list.filter((p) => p.status === 'active');
  const completed = list.filter((p) => p.status === 'completed');

  return (
    <Page>
      <PageHeader
        kicker="practices"
        title="Three structured paths."
        subtitle="Start one, stay with it. You own the words. The system holds the shape."
      />

      <div className="grid sm:grid-cols-3 gap-4">
        {(['forgiveness', 'compassion', 'narrative'] as const).map((k) => (
          <Card
            key={k}
            interactive
            onClick={() => {
              if (k === 'forgiveness') {
                setPendingKind(k);
              } else {
                start.mutate({ kind: k });
              }
            }}
          >
            <CardHeader>
              <CardTitle kicker={KIND_DESC[k]!.kicker}>{KIND_DESC[k]!.label}</CardTitle>
              <CardDescription>{KIND_DESC[k]!.oneLiner}</CardDescription>
            </CardHeader>
          </Card>
        ))}
      </div>

      {pendingKind === 'forgiveness' && (
        <div className="mt-6 rounded-[var(--radius-lg)] border border-border bg-muted/40 p-5 animate-rise">
          <div className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
            name them
          </div>
          <h3 className="font-serif text-lg mt-1">
            Who is the version of you you&apos;re working to forgive?
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            a name, a phrase, &quot;the one who…&quot;.
          </p>
          <Input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="e.g. the one who took the job"
            className="mt-4"
            autoFocus
          />
          <div className="mt-4 flex gap-2">
            <Button
              size="sm"
              disabled={!subject.trim() || start.isPending}
              onClick={() => start.mutate({ kind: 'forgiveness', subject: subject.trim() })}
            >
              Begin
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setPendingKind(null);
                setSubject('');
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {active.length > 0 && (
        <Section title="in progress">
          <ul className="space-y-2">
            {active.map((p) => (
              <PracticeRow key={p.id} p={p} />
            ))}
          </ul>
        </Section>
      )}
      {completed.length > 0 && (
        <Section title="completed">
          <ul className="space-y-2">
            {completed.map((p) => (
              <PracticeRow key={p.id} p={p} />
            ))}
          </ul>
        </Section>
      )}
      {active.length === 0 && completed.length === 0 && (
        <div className="mt-10">
          <EmptyState
            title="No practices yet."
            description="Pick one above. They're designed to be entered slowly. None of them require completion to be useful."
          />
        </div>
      )}
    </Page>
  );
}

function PracticeRow({ p }: { p: PracticeSummary }) {
  return (
    <li>
      <Link
        to="/practices/$id"
        params={{ id: p.id }}
        className="flex items-center justify-between gap-3 rounded-[var(--radius)] border border-border bg-background px-4 py-3 hover:bg-muted/60 transition-colors"
      >
        <div className="min-w-0">
          <span className="font-serif text-[15px]">{KIND_DESC[p.kind]?.label ?? p.kind}</span>
          {p.subject && (
            <span className="ml-2 text-sm text-muted-foreground italic">· {p.subject}</span>
          )}
        </div>
        <Badge variant="outline">
          {p.status === 'completed'
            ? 'completed'
            : `step ${p.completedSteps}/${p.totalSteps}`}
        </Badge>
      </Link>
    </li>
  );
}
